<template>
  <div>
    <BaseToolBar
      title="Contraventions"
      subtitle="Historique des contraventions du véhicule"
    >
      <BaseButton
        class="btn btn-primary ml-3"
        v-tooltip="'Ajouter une contravention'"
        @click="addContravention"
        :loading="loading"
        icon="plus"
        text="Ajouter"
      ></BaseButton>
    </BaseToolBar>

    <!-- Feedback message -->
    <div v-if="feedback.message" :class="`alert alert-${feedback.type} mt-3`">
      {{ feedback.message }}
    </div>

    <!-- Liste des contraventions -->
    <div
      v-for="(contravention, index) in vehicule.contraventions"
      :key="contravention.id || 'new_' + index"
      class="card mb-3"
      :class="{ 'border-warning': isContraventionEnAttente(contravention) }"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center"
        :class="{ 'bg-warning': isContraventionEnAttente(contravention) }"
      >
        <h6 class="mb-0">
          {{ getTypeContraventionLabel(contravention.type) }} {{ index + 1 }}
          <span
            :class="getBadgeClassByType(contravention.type)"
            class="badge ml-2"
          >
            {{ getTypeContraventionLabel(contravention.type) }}
          </span>
          <span v-if="contravention.date" class="badge badge-secondary ml-2">
            {{ formatDate(contravention.date) }}
          </span>
          <span
            :class="getStatutBadgeClass(contravention.statut)"
            class="badge ml-2"
          >
            {{ getStatutLabel(contravention.statut) }}
          </span>
        </h6>
        <div>
          <ButtonCircle
            v-if="
              !contraventionEnCoursEdition ||
                contraventionEnCoursEdition.id !== contravention.id
            "
            name="edit"
            @click="editContravention(contravention)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Modifier'"
          ></ButtonCircle>
          <ButtonCircle
            name="trash"
            @click="deleteContravention(contravention)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Supprimer'"
          ></ButtonCircle>
        </div>
      </div>
      <div class="card-body">
        <!-- Vue édition -->
        <div
          v-if="
            contraventionEnCoursEdition &&
              contraventionEnCoursEdition.id === contravention.id
          "
        >
          <base-form-row row>
            <base-select
              v-model.number="contraventionEnCoursEdition.type"
              inputText="Type de contravention"
              :options="typesContravention"
            ></base-select>
            <base-inputDatePicker
              v-model="contraventionEnCoursEdition.date"
              inputText="Date de l’avis de contravention"
            ></base-inputDatePicker>
          </base-form-row>
          <base-form-row row>
            <base-input
              v-model="contraventionEnCoursEdition.numero_avis"
              inputText="Numéro d'avis"
            ></base-input>
            <base-select
              v-model.number="contraventionEnCoursEdition.cause_infraction"
              inputText="Cause de l'infraction"
              :options="causesInfraction"
            ></base-select>
          </base-form-row>
          <base-form-row row>
            <base-input
              v-model.number="contraventionEnCoursEdition.montant_amende"
              inputText="Montant de l'amende (€)"
              type="number"
              step="0.01"
            ></base-input>
            <base-input
              v-model.number="contraventionEnCoursEdition.nombre_points_perdus"
              inputText="Points perdus"
              type="number"
              min="0"
              max="12"
            ></base-input>
          </base-form-row>
          <base-form-row row>
            <select-collaborateur
              class="mb-1"
              v-model.number="contraventionEnCoursEdition.collaborateur_id"
              :text="'Collaborateur concerné'"
            ></select-collaborateur>
            <base-select
              v-model.number="contraventionEnCoursEdition.statut"
              inputText="Statut"
              :options="statutsContravention"
            ></base-select>
          </base-form-row>
          <base-form-row row>
            <base-inputDatePicker
              v-model="contraventionEnCoursEdition.date_designation"
              inputText="Date de désignation"
              :disabled="contraventionEnCoursEdition.statut < 2"
            ></base-inputDatePicker>
            <base-inputDatePicker
              v-model="contraventionEnCoursEdition.date_paiement"
              inputText="Date de paiement"
              :disabled="contraventionEnCoursEdition.statut !== 3"
            ></base-inputDatePicker>
          </base-form-row>
          <base-form-row>
            <base-input
              v-model="contraventionEnCoursEdition.commentaires"
              inputText="Commentaires"
              type="textarea"
              rows="3"
            ></base-input>
          </base-form-row>

          <div class="d-flex justify-content-end mt-3">
            <button
              class="btn btn-secondary mr-2"
              @click="cancelEdit()"
              :disabled="loading"
            >
              Annuler
            </button>
            <button
              class="btn btn-primary"
              @click="saveContraventionEnCours()"
              :disabled="loading"
            >
              <i class="fas fa-save mr-1"></i>
              Enregistrer
            </button>
          </div>
        </div>

        <!-- Vue normale (non-édition) -->
        <div v-else>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Type:</b> {{ getTypeContraventionLabel(contravention.type) }}
            </div>
            <div class="col-md-6">
              <b>Date de l'infraction:</b>
              {{
                contravention.date
                  ? formatDate(contravention.date)
                  : "Non définie"
              }}
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Numéro d'avis:</b>
              {{ contravention.numero_avis || "Non défini" }}
            </div>
            <div class="col-md-6">
              <b>Cause:</b>
              {{ getCauseInfractionLabel(contravention.cause_infraction) }}
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Montant:</b> {{ formatCurrency(contravention.montant_amende) }}
            </div>
            <div class="col-md-6">
              <b>Points perdus:</b>
              {{ contravention.nombre_points_perdus || 0 }}
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Collaborateur concerné:</b>
              <select-collaborateur
                class="mb-1"
                v-model.number="contravention.collaborateur_id"
                :text="'Collaborateur concerné'"
              ></select-collaborateur>
              {{ getCollaborateurName(contravention.collaborateur_id) }}
            </div>
            <div class="col-md-6">
              <b>Statut:</b>
              <span :class="getStatutTextClass(contravention.statut)">
                {{ getStatutLabel(contravention.statut) }}
              </span>
            </div>
          </div>
          <div class="row mb-3" v-if="contravention.statut >= 2">
            <div class="col-md-6" v-if="contravention.date_designation">
              <b>Désigné le:</b>
              {{ formatDate(contravention.date_designation) }}
            </div>
            <div class="col-md-6" v-if="contravention.date_paiement">
              <b>Payé le:</b> {{ formatDate(contravention.date_paiement) }}
            </div>
          </div>
          <div class="row mb-3" v-if="contravention.commentaires">
            <div class="col-md-12">
              <b>Commentaires:</b> {{ contravention.commentaires }}
            </div>
          </div>

          <!-- Boutons d'action rapide selon le statut -->
          <div class="row mt-4" v-if="!isContraventionFermee(contravention)">
            <div class="col-md-12 d-flex">
              <button
                v-if="contravention.statut === 0"
                class="btn btn-sm btn-outline-primary mr-2"
                @click="actionMarquerADesigner(contravention)"
              >
                <i class="fas fa-user-edit mr-1"></i> Marquer à désigner
              </button>
              <button
                v-if="contravention.statut === 1"
                class="btn btn-sm btn-outline-success mr-2"
                @click="actionDesigner(contravention)"
              >
                <i class="fas fa-user-check mr-1"></i> Désigner
              </button>
              <button
                v-if="contravention.statut < 3"
                class="btn btn-sm btn-outline-info"
                @click="actionMarquerPayee(contravention)"
              >
                <i class="fas fa-money-bill-wave mr-1"></i> Marquer comme payée
              </button>
            </div>
          </div>
        </div>

        <div class="pt-3" v-if="contravention.id">
          <uploader
            :id="'doc_vehicule_contravention_' + contravention.id"
            url="/upload/document"
            file_system="VEHICULES_CONTRAVENTIONS"
            file_field="contravention"
            :file_key="contravention.id"
            title="Pièces jointes"
          ></uploader>
        </div>
      </div>
    </div>

    <div
      v-if="!vehicule.contraventions || vehicule.contraventions.length === 0"
      class="alert alert-info"
    >
      Ce véhicule n'a pas encore de contravention enregistrée. Cliquez sur
      "Ajouter" pour ajouter une contravention.
    </div>

    <!-- Tableau récapitulatif -->
    <div class="mt-4">
      <h5>Récapitulatif par statut</h5>
      <table class="table table-bordered table-hover">
        <thead class="thead-light">
          <tr>
            <th>Statut</th>
            <th>Nombre</th>
            <th>Montant total</th>
            <th>Points perdus</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="statut in statutsContravention" :key="statut.id">
            <td>
              <span :class="getStatutTextClass(statut.id)">
                {{ statut.description }}
              </span>
            </td>
            <td>{{ countContraventionByStatut(statut.id) }}</td>
            <td>{{ formatCurrency(totalMontantByStatut(statut.id)) }}</td>
            <td>{{ totalPointsPerdusByStatut(statut.id) }}</td>
          </tr>
          <tr class="font-weight-bold bg-light">
            <td>Total</td>
            <td>
              {{ vehicule.contraventions ? vehicule.contraventions.length : 0 }}
            </td>
            <td>{{ formatCurrency(totalMontant) }}</td>
            <td>{{ totalPointsPerdus }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex justify-content-between mt-4">
      <div class="card text-white bg-info mb-3" style="max-width: 18rem;">
        <div class="card-header">Statistiques</div>
        <div class="card-body">
          <h5 class="card-title">
            {{ vehicule.contraventions ? vehicule.contraventions.length : 0 }}
            contravention(s)
          </h5>
          <p class="card-text">
            {{ getContraventionsEnAttenteCount() }} en attente<br />
            {{ getContraventionsADesignerCount() }} à désigner<br />
            {{ getContraventionsDesigneeCount() }} désignée(s)<br />
            {{ getContraventionsPayeeCount() }} payée(s)
          </p>
        </div>
      </div>

      <div
        v-if="hasContraventionsEnAttente()"
        class="card text-dark bg-warning mb-3"
        style="max-width: 24rem;"
      >
        <div class="card-header">
          <i class="fas fa-exclamation-circle mr-1"></i>
          Attention
        </div>
        <div class="card-body">
          <h5 class="card-title">
            Actions requises
          </h5>
          <p class="card-text">
            Ce véhicule possède
            {{ getContraventionsEnAttenteCount() }} contravention(s) nécessitant
            une action (paiement ou désignation).
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseFormRow from "@/components/bases/FormRow.vue";
import BaseInput from "@/components/bases/Input.vue";
import BaseSelect from "@/components/bases/Select.vue";
import BaseInputDatePicker from "@/components/bases/InputDatePicker.vue";
import BaseButton from "@/components/bases/Button.vue";
import ButtonCircle from "@/components/bases/ButtonCircle.vue";
import Uploader from "@/components/bases/Uploader.vue";

import SelectCollaborateur from "@/components/bases/SelectCollaborateur.vue";

export default {
  name: "VehiculeContraventions",
  components: {
    BaseToolBar,
    BaseFormRow,
    BaseInput,
    BaseSelect,
    BaseInputDatePicker,
    BaseButton,
    ButtonCircle,
    Uploader,
    SelectCollaborateur
  },
  props: {
    vehicule: {
      type: Object,
      required: true
    },
    statut: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      contraventionEnCoursEdition: null,
      isLoading: false,
      feedback: {
        message: "",
        type: "info"
      },
      typesContravention: [
        { id: 0, description: "Avis de contravention" },
        { id: 1, description: "FPS" },
        { id: 2, description: "Avis de paiement des péages" }
      ],
      causesInfraction: [
        { id: 0, description: "Excès de vitesse" },
        { id: 1, description: "Conduite dangereuse" },
        { id: 2, description: "Appel au volant" },
        { id: 3, description: "Autre" }
      ],
      statutsContravention: [
        { id: 0, description: "En attente de paiement" },
        { id: 1, description: "À désigner" },
        { id: 2, description: "Désigné" },
        { id: 3, description: "Payée" }
      ]
    };
  },
  computed: {
    collaborateursOptions() {
      if (!this.vehicule.collaborateurs) return [];

      return this.vehicule.collaborateurs
        .filter(collab => collab && collab.collaborateur)
        .map(collab => {
          return {
            id: collab.collaborateur.id,
            description: `${collab.collaborateur.collaborateur_nom} ${collab
              .collaborateur.collaborateur_prenom || ""}`
          };
        })
        .sort((a, b) => a.description.localeCompare(b.description));
    },
    totalMontant() {
      if (
        !this.vehicule.contraventions ||
        this.vehicule.contraventions.length === 0
      )
        return 0;

      return this.vehicule.contraventions.reduce((total, contravention) => {
        return total + (parseFloat(contravention.montant_amende) || 0);
      }, 0);
    },
    totalPointsPerdus() {
      if (
        !this.vehicule.contraventions ||
        this.vehicule.contraventions.length === 0
      )
        return 0;

      return this.vehicule.contraventions.reduce((total, contravention) => {
        return total + (parseInt(contravention.nombre_points_perdus) || 0);
      }, 0);
    }
  },
  mounted() {
    // Initialiser la liste des contraventions si elle n'existe pas
    if (!this.vehicule.contraventions) {
      this.$set(this.vehicule, "contraventions", []);
    }
  },
  methods: {
    addContravention() {
      // Vérifier que le véhicule est bien enregistré
      if (!this.vehicule.id) {
        this.showFeedback(
          "Veuillez d'abord enregistrer le véhicule",
          "warning"
        );
        return;
      }

      // Fermer toute édition en cours
      this.contraventionEnCoursEdition = null;

      // Créer immédiatement une contravention en base de données
      this.isLoading = true;

      this.$http
        .post("/vehicules-contravention", {
          vehicule_id: this.vehicule.id,
          statut: 0
        })
        .then(response => {
          // Vérifier que contraventions est initialisé
          if (!this.vehicule.contraventions) {
            this.$set(this.vehicule, "contraventions", []);
          }

          // Ajouter la contravention retournée par l'API
          this.vehicule.contraventions.unshift(response.data);

          // Passer en mode édition sur cette nouvelle contravention
          this.editContravention(response.data);

          this.showFeedback(
            "Contravention créée avec succès. Vous pouvez maintenant compléter les détails.",
            "success"
          );
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    editContravention(contravention) {
      // Créer une copie pour ne pas modifier directement l'original
      this.contraventionEnCoursEdition = { ...contravention };
    },
    cancelEdit() {
      this.contraventionEnCoursEdition = null;
    },
    saveContraventionEnCours() {
      if (
        !this.contraventionEnCoursEdition ||
        !this.contraventionEnCoursEdition.id
      )
        return;

      // Valider les dates selon le statut
      if (
        this.contraventionEnCoursEdition.statut >= 2 &&
        !this.contraventionEnCoursEdition.date_designation
      ) {
        this.showFeedback(
          "Veuillez spécifier une date de désignation",
          "warning"
        );
        return;
      }

      if (
        this.contraventionEnCoursEdition.statut === 3 &&
        !this.contraventionEnCoursEdition.date_paiement
      ) {
        this.showFeedback("Veuillez spécifier une date de paiement", "warning");
        return;
      }

      this.isLoading = true;
      this.$http
        .put(
          "/vehicules-contravention/" + this.contraventionEnCoursEdition.id,
          this.contraventionEnCoursEdition
        )
        .then(response => {
          // Mettre à jour la contravention dans le tableau
          const index = this.vehicule.contraventions.findIndex(
            c => c.id === this.contraventionEnCoursEdition.id
          );
          if (index !== -1) {
            this.$set(this.vehicule.contraventions, index, response.data);
          }
          this.contraventionEnCoursEdition = null;
          this.showFeedback("Contravention mise à jour avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deleteContravention(contravention) {
      if (
        !window.confirm(
          "Êtes-vous sûr de vouloir supprimer cette contravention ?"
        )
      ) {
        return;
      }

      // Si c'est une nouvelle contravention non encore sauvegardée
      if (!contravention.id) {
        const index = this.vehicule.contraventions.findIndex(
          c => c === contravention
        );
        if (index !== -1) {
          this.vehicule.contraventions.splice(index, 1);
        }
        return;
      }

      this.isLoading = true;
      this.$http
        .delete(`/vehicules-contravention/${contravention.id}`)
        .then(() => {
          const index = this.vehicule.contraventions.findIndex(
            c => c.id === contravention.id
          );
          if (index !== -1) {
            this.vehicule.contraventions.splice(index, 1);
          }
          this.showFeedback("Contravention supprimée avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Actions rapides
    actionMarquerADesigner(contravention) {
      const updatedContravention = { ...contravention, statut: 1 };
      this.updateContraventionStatus(updatedContravention);
    },
    actionDesigner(contravention) {
      const today = this.$moment().format("YYYY-MM-DD");
      const updatedContravention = {
        ...contravention,
        statut: 2,
        date_designation: today
      };
      this.updateContraventionStatus(updatedContravention);
    },
    actionMarquerPayee(contravention) {
      const today = this.$moment().format("YYYY-MM-DD");
      const updatedContravention = {
        ...contravention,
        statut: 3,
        date_paiement: today
      };
      this.updateContraventionStatus(updatedContravention);
    },
    updateContraventionStatus(contravention) {
      this.isLoading = true;
      this.$http
        .put(`/vehicules-contravention/${contravention.id}`, contravention)
        .then(response => {
          const index = this.vehicule.contraventions.findIndex(
            c => c.id === contravention.id
          );
          if (index !== -1) {
            this.$set(this.vehicule.contraventions, index, response.data);
          }
          this.showFeedback("Statut mis à jour avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Méthodes utilitaires
    getTypeContraventionLabel(typeId) {
      const type = this.typesContravention.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },
    getCauseInfractionLabel(causeId) {
      const cause = this.causesInfraction.find(c => c.id === parseInt(causeId));
      return cause ? cause.description : "Non définie";
    },
    getStatutLabel(statutId) {
      const statut = this.statutsContravention.find(
        s => s.id === parseInt(statutId)
      );
      return statut ? statut.description : "Non défini";
    },
    getCollaborateurName(collaborateurId) {
      if (!collaborateurId) return "Non désigné";

      const option = this.collaborateursOptions.find(
        c => c.id === collaborateurId
      );
      return option ? option.description : "Collaborateur inconnu";
    },
    countContraventionByStatut(statutId) {
      if (!this.vehicule.contraventions) return 0;
      return this.vehicule.contraventions.filter(
        c => parseInt(c.statut) === statutId
      ).length;
    },
    totalMontantByStatut(statutId) {
      if (!this.vehicule.contraventions) return 0;
      return this.vehicule.contraventions
        .filter(c => parseInt(c.statut) === statutId)
        .reduce((total, c) => total + (parseFloat(c.montant_amende) || 0), 0);
    },
    totalPointsPerdusByStatut(statutId) {
      if (!this.vehicule.contraventions) return 0;
      return this.vehicule.contraventions
        .filter(c => parseInt(c.statut) === statutId)
        .reduce(
          (total, c) => total + (parseInt(c.nombre_points_perdus) || 0),
          0
        );
    },
    isContraventionEnAttente(contravention) {
      return parseInt(contravention.statut) < 3;
    },
    isContraventionFermee(contravention) {
      return parseInt(contravention.statut) === 3;
    },
    hasContraventionsEnAttente() {
      if (
        !this.vehicule.contraventions ||
        this.vehicule.contraventions.length === 0
      )
        return false;
      return this.vehicule.contraventions.some(c => parseInt(c.statut) < 3);
    },
    getContraventionsEnAttenteCount() {
      return this.countContraventionByStatut(0);
    },
    getContraventionsADesignerCount() {
      return this.countContraventionByStatut(1);
    },
    getContraventionsDesigneeCount() {
      return this.countContraventionByStatut(2);
    },
    getContraventionsPayeeCount() {
      return this.countContraventionByStatut(3);
    },
    getBadgeClassByType(typeId) {
      typeId = parseInt(typeId);
      switch (typeId) {
        case 0:
          return "badge-danger"; // Avis de contravention
        case 1:
          return "badge-warning"; // FPS
        case 2:
          return "badge-primary"; // Avis de paiement des péages
        default:
          return "badge-secondary";
      }
    },
    getStatutBadgeClass(statutId) {
      statutId = parseInt(statutId);
      switch (statutId) {
        case 0:
          return "badge-warning"; // En attente de paiement
        case 1:
          return "badge-danger"; // À désigner
        case 2:
          return "badge-info"; // Désigné
        case 3:
          return "badge-success"; // Payée
        default:
          return "badge-secondary";
      }
    },
    getStatutTextClass(statutId) {
      statutId = parseInt(statutId);
      switch (statutId) {
        case 0:
          return "text-warning"; // En attente de paiement
        case 1:
          return "text-danger"; // À désigner
        case 2:
          return "text-info"; // Désigné
        case 3:
          return "text-success"; // Payée
        default:
          return "text-secondary";
      }
    },
    formatDate(date) {
      if (!date) return "";
      return this.$moment(date).format("DD/MM/YYYY");
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR"
      }).format(value || 0);
    },
    showFeedback(message, type = "info") {
      this.feedback = { message, type };
      // Faire disparaître le message après quelques secondes
      setTimeout(() => {
        this.feedback = { message: "", type: "info" };
      }, 3000);
    }
  }
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}

.badge-primary {
  background-color: #007bff;
  color: #fff;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-danger {
  background-color: #dc3545;
  color: #fff;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

.badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-success {
  color: #28a745 !important;
}

.text-secondary {
  color: #6c757d !important;
}

.border-warning {
  border-color: #ffc107 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
}

.border-danger {
  border-color: #dc3545 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.thead-light th {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}
</style>
